// ============================================================
// SpringBootJWT.jsx
//
// A branded, responsive course content page for the
// "JWT Authentication in Spring Boot" lesson.
//
// Author: ProDev Enterprise Series (Java Branch)
// Features: Stateless architecture, JJWT integration,
//           and SecurityContext orchestration.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import VpnKeyIcon             from "@mui/icons-material/VpnKey";
import ShieldIcon             from "@mui/icons-material/Shield";
import TokenIcon              from "@mui/icons-material/Token";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import LockOpenIcon           from "@mui/icons-material/LockOpen";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function SpringBootJWT() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<ShieldIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Java Enterprise · Module 17"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        JWT <span>Authentication</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Implement stateless security using JSON Web Tokens. JWT allows your backend to verify user identity without maintaining heavy server-side sessions, making it the industry standard for microservices and modern SPAs.
                    </Typography>
                </Box>

                {/* 1. JWT STRUCTURE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TokenIcon />} label="1. Anatomy of a Token" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        A JWT consists of three parts separated by dots: Header, Payload, and Signature. The signature ensures the token hasn't been tampered with.
                    </Typography>
                    <CodeBlock
                        title="JWT Composition"
                        code={`// Format: xxxxx.yyyyy.zzzzz\nHeader:  {"alg": "HS512", "typ": "JWT"}\nPayload: {"sub": "admin", "iat": 1625097600, "exp": 1625184000}\nSignature: HMACSHA512(Header + "." + Payload, SECRET_KEY)`}
                    />
                </Box>

                {/* 2. TOKEN UTILS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<VpnKeyIcon />} label="2. Generation & Validation" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Utilize the JJWT library to handle the complex crypto operations of signing and parsing claims.
                    </Typography>
                    <CodeBlock
                        title="JwtProvider.java"
                        code={`// Generate Token\nString jwt = Jwts.builder()\n    .setSubject(username)\n    .signWith(SignatureAlgorithm.HS512, secretKey)\n    .compact();\n\n// Parse Claims\nClaims claims = Jwts.parser()\n    .setSigningKey(secretKey)\n    .parseClaimsJws(token)\n    .getBody();`}
                    />
                </Box>

                {/* 3. SECURITY FILTER */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LockOpenIcon />} label="3. Stateless Security Filter" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Intercept every request to extract the <strong>Bearer</strong> token from the Authorization header and populate the Spring Security Context.
                    </Typography>
                    <CodeBlock
                        title="JwtRequestFilter.java"
                        code={`if (token != null && jwtUtils.validate(token)) {\n    String user = jwtUtils.getUsername(token);\n    var auth = new UsernamePasswordAuthenticationToken(user, null, authorities);\n    SecurityContextHolder.getContext().setAuthentication(auth);\n}`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="JWT Implementation Highlights" />
                    <Grid container spacing={1.5}>
                        {[
                            "Store SECRET_KEY in environment variables, never in source code",
                            "Use short expiration times (e.g., 15-60 mins) to limit risk",
                            "Implement Refresh Tokens for a seamless user experience",
                            "Disable CSRF in Spring Security as JWT is not cookie-based",
                            "Set SessionCreationPolicy to STATELESS in SecurityFilterChain",
                            "Always verify the signature before trusting payload data"
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/java-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Return to Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> JWTs are Base64 encoded, not encrypted. This means anyone can read the <strong>Payload</strong> contents. Never put sensitive data like raw passwords or SSNs inside the token claims!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
